/**
 * Outdoor high-contrast mode.
 *
 * A WCAG-AAA accessibility overlay for direct-sunlight field use — NOT a light
 * theme (Midnight #020617 stays the only theme per project standards). It flips
 * a `data-contrast="outdoor"` attribute on <html>; the paired token set in
 * index.css swaps the readability-critical variables to a pure-black / pure-white
 * (21:1) palette with the brightest AAA status chips.
 */
export type ContrastMode = 'midnight' | 'outdoor';

const CONTRAST_KEY = 'dd_contrast_mode';
const CONTRAST_ATTR = 'data-contrast';
export const CONTRAST_CHANGE_EVENT = 'dd:contrast-change';

export function getContrastMode(): ContrastMode {
  if (typeof document !== 'undefined') {
    if (document.documentElement.getAttribute(CONTRAST_ATTR) === 'outdoor') return 'outdoor';
  }
  try {
    return localStorage.getItem(CONTRAST_KEY) === 'outdoor' ? 'outdoor' : 'midnight';
  } catch {
    return 'midnight';
  }
}

function applyMode(mode: ContrastMode): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (mode === 'outdoor') root.setAttribute(CONTRAST_ATTR, 'outdoor');
  else root.removeAttribute(CONTRAST_ATTR);
}

export function setContrastMode(mode: ContrastMode): void {
  applyMode(mode);
  try {
    if (mode === 'outdoor') localStorage.setItem(CONTRAST_KEY, 'outdoor');
    else localStorage.removeItem(CONTRAST_KEY);
  } catch {
    /* storage may be blocked (private mode) — attribute is still applied */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<ContrastMode>(CONTRAST_CHANGE_EVENT, { detail: mode }));
  }
}

export function toggleContrastMode(): ContrastMode {
  const next: ContrastMode = getContrastMode() === 'outdoor' ? 'midnight' : 'outdoor';
  setContrastMode(next);
  return next;
}

/** Apply the persisted contrast mode before React mounts (avoids a flash). */
export function applyPersistedContrast(): void {
  try {
    if (localStorage.getItem(CONTRAST_KEY) === 'outdoor') applyMode('outdoor');
  } catch {
    /* ignore */
  }
}
